import React, { useState } from 'react';
import { Autocomplete, Box, FormControl, Popper, TextField } from '@mui/material';
import { makeStyles } from '@mui/styles';
import Flex from 'src/components/BeaconV2Components/Flex/Flex';
import { SlPill } from 'src/components/BeaconV2Components/SlPill';
import {
  SelectOption,
  FormValue
} from 'src/components/BeaconV2Components/ExperimentalLayout/Settings/Presentations/types';
import { Text } from 'src/components/BeaconV2Components/Generic/Text';
import { AppImage, useStacklineTheme } from '@stackline/ui';
import { BlueCancel } from 'src/components/SvgIcons';
import { Control, Controller } from 'react-hook-form';
import { DynamicDownChevron } from 'src/components/BeaconV2Components/Header/SLDropdownMenu/DynamicDownChevron';
import colors from 'src/utils/colors';

interface SearchSelectProps {
  title: string;
  placeholder: string;
  selectOptions: SelectOption[];
  isMulti?: boolean;
  /** Maximum number of items that can be selected. When 1, selecting a new item replaces the existing one. */
  maxCount?: number;
  name: string;
  control: Control<any>;
  onSelectionChange?: (newValue: any) => void;
}

const useStyles = makeStyles({
  paper: {
    margin: '8px 0',
    border: `1px solid ${colors.graysSecondary}`,
    borderRadius: '4px',
    boxShadow: '0px 4px 14px 0px #0000001A'
  },
  listbox: {
    padding: 0,
    maxHeight: '294px',
    overflow: 'auto',
    scrollbarColor: 'rgba(190, 199, 211, 1) transparent',
    scrollbarWidth: 'thin'
  },
  option: {
    color: colors.darkBlue,
    '&:not(:last-child)': {
      borderBottom: `1px solid ${colors.graysSecondary}`
    },
    '&.MuiAutocomplete-option': {
      lineHeight: '20px',
      padding: '8px 12px'
    }
  },
  noOptions: {
    padding: '8px 12px',
    color: colors.grey,
    WebkitFontSmoothing: 'antialiased',
    fontStyle: 'italic'
  },
  root: {
    '&.Mui-expanded .search-select-chevron': {
      transform: 'rotate(180deg)'
    }
  }
});

export const SearchSelect = ({ title, placeholder, selectOptions, isMulti, maxCount, name, control, onSelectionChange = () => {} }: SearchSelectProps) => {
  const theme = useStacklineTheme();
  const classes = useStyles();
  const [inputValue, setInputValue] = useState('');

  return (
    <Flex flexDirection="column" gap={8}>
      <Text variant="h6" sx={{ fontSize: '14px' }}>
        {title}
      </Text>

      <Controller
        control={control}
        name={name}
        render={({ field: { value, onChange }, fieldState: { error } }) => {
          const selectedValue = isMulti
            ? selectOptions.filter((option) => (value as FormValue[]).some((val) => val.id === option.value))
            : selectOptions.find((option) => value?.id === option.value);

          const filteredOptions = isMulti
            ? selectOptions.filter((option) => !(value as FormValue[]).some((val) => val.id === option.value))
            : selectOptions.filter((option) => value?.id !== option.value);

          const handleChange = (_, newValue) => {
            if (!isMulti) {
              const value = newValue ? { id: newValue.value, name: newValue.displayName } : { id: '', name: '' };
              onChange(value);
              onSelectionChange(value);
              setInputValue('');
              return;
            }

            let resultValue = newValue ? newValue.map((option) => ({ id: option.value, name: option.displayName })) : [];
            
            // If maxCount is set and we're over the limit, keep only the last selected items
            if (maxCount !== undefined && resultValue.length > maxCount) {
              // Keep only the most recently added items (last maxCount items)
              resultValue = resultValue.slice(-maxCount);
            }
            
            onChange(resultValue);
            onSelectionChange(resultValue);
            setInputValue('');
          };

          const handlePillRemove = (optionValue) => {
            const updatedValue = isMulti
              ? (value as FormValue[]).filter((val) => val.id !== optionValue)
              : { id: '', name: '' };
            onSelectionChange(updatedValue);
            onChange(updatedValue);
            setInputValue('');
          };

          let pills = selectedValue as SelectOption[];

          if (!isMulti) {
            pills = selectedValue ? [selectedValue as SelectOption] : [];
          }

          return (
            <FormControl error={!!error}>
              <Flex flexDirection="column" gap={12}>
                <Autocomplete
                  multiple={isMulti}
                  options={filteredOptions}
                  getOptionLabel={(option) => option.displayName}
                  value={selectedValue}
                  onChange={handleChange}
                  inputValue={inputValue}
                  openOnFocus
                  onInputChange={(_, newInputValue, reason) => {
                    if (reason === 'input') {
                      setInputValue(newInputValue);
                    }
                  }}
                  disableClearable
                  noOptionsText="No search results found"
                  classes={classes}
                  forcePopupIcon={false}
                  PopperComponent={(props) => (
                    <Popper {...props} style={{ width: 'fit-content' }} placement="bottom-start" />
                  )}
                  renderInput={(selected) => {
                    return (
                      <TextField
                        hiddenLabel
                        variant="standard"
                        {...selected}
                        sx={{
                          '& div.MuiInputBase-root': {
                            padding: 0,
                            cursor: 'pointer'
                          },
                          '& div.MuiInput-root': {
                            display: 'flex',
                            alignItems: 'center',
                            boxSizing: 'border-box',
                            borderRadius: 1,
                            border: `1px solid ${theme.colors.primaryGray}`,
                            padding: '8px 8px 8px 12px',
                            width: '100%',
                            backgroundColor: theme.colors.white,
                            '& .MuiInput-input': {
                              padding: 0,
                              fontSize: '14px',
                              fontWeight: 400,
                              color: theme.colors.primary,
                              cursor: 'pointer'
                            }
                          },
                          '.MuiInput-input::placeholder': {
                            color: theme.colors.secondary,
                            fontWeight: 400,
                            WebkitFontSmoothing: 'antialiased',
                            fontSize: '14px'
                          }
                        }}
                        InputProps={{
                          ...selected.InputProps,
                          disableUnderline: true,
                          startAdornment: (
                            <AppImage
                              src="/svg/icons/search.svg"
                              alt="search-icon"
                              style={{
                                pointerEvents: 'none',
                                marginRight: 8,
                                height: 14,
                                width: 14
                              }}
                            />
                          ),
                          endAdornment: (
                            <DynamicDownChevron
                              strokeWidth="2px"
                              color={theme.colors.primary}
                              style={{
                                verticalAlign: 'middle',
                                pointerEvents: 'none'
                              }}
                            />
                          ),
                          autoComplete: 'off',
                          id: 'type-search'
                        }}
                        placeholder={placeholder}
                        color="primary"
                      />
                    );
                  }}
                ></Autocomplete>
                {!!pills.length && (
                  <Flex sx={{ gap: '12px 8px' }} flexWrap="wrap">
                    {pills.map((option) => (
                      <SlPill
                        key={option.id}
                        sx={{
                          background: '#EDF5FF',
                          border: 'none',
                          padding: '3px 12px',
                          fontSize: '12px',
                          fontWeight: 400,
                          height: 'auto',
                          lineHeight: '18px'
                        }}
                      >
                        <Flex gap={8} alignItems="center">
                          {option.displayName}
                          <BlueCancel
                            width={12}
                            height={12}
                            style={{ overflow: 'visible' }}
                            onClick={() => handlePillRemove(option.value)} // Handle pill removal
                          />
                        </Flex>
                      </SlPill>
                    ))}
                  </Flex>
                )}
              </Flex>
            </FormControl>
          );
        }}
      />
    </Flex>
  );
};
